import { useState, useEffect, useContext } from 'react';
import { GlobalAppContext } from 'src/components/App';
import {
    PromotionComponentType,
    PromotionStatus,
    PromotionStageInfo,
    PromoteComponentResponse,
    PROMOTION_NOT_FOUND,
    PROMOTION_POLL_INTERVAL_MS,
    MAX_PROMOTION_POLL_ATTEMPTS,
    IN_PROGRESS_STATUSES,
} from 'src/models/promotion/PromotionTypes';
import {getFeatureFlags} from "src/services/FeatureFlagsService";

interface UsePromotionArgs {
    componentType: PromotionComponentType;
    componentId: string;
    componentName: string;
    stageInfo: PromotionStageInfo | null;
    onPromotionComplete?: () => void;
}

export interface UsePromotionResult {
    /** True while a promotion is in progress (inProgressPromotionId is set). */
    isPromoting: boolean;
    /** True until the initial GetPromotionStatus lookup resolves. */
    initializing: boolean;
    /** completedAt of the component's latest promotion, if any. */
    lastPromotedAt: string | null;
    modalVisible: boolean;
    openModal: () => void;
    closeModal: () => void;
    /** Called with the Promote API response when the user confirms in the modal. */
    startPromotion: (response: PromoteComponentResponse) => void;
    featureEnabled: boolean | null;
}

/**
 * Promotion lifecycle for PromoteButton, driven by two state variables:
 *
 * - promotionStatus: latest known status of the component's promotion.
 *   null → initial lookup still loading; PROMOTION_NOT_FOUND → no promotion exists.
 * - inProgressPromotionId: the promotionId currently being polled.
 *   Non-null means a promotion is in progress → button shows "Promoting...", polling runs.
 *   Set to null on terminal results → button goes idle, polling stops.
 *
 * Both are set from exactly two sources: the initial GetPromotionStatus lookup
 * (componentId + componentType, silent) and the Promote API response (startPromotion).
 * While inProgressPromotionId is set, a polling effect keeps the status fresh;
 * any terminal result it receives is announced immediately.
 */
export function usePromotion(args: UsePromotionArgs): UsePromotionResult {
    const { componentType, componentId, componentName, stageInfo, onPromotionComplete } = args;
    const { services } = useContext(GlobalAppContext);

    const [modalVisible, setModalVisible] = useState(false);
    const [promotionStatus, setPromotionStatus] = useState<PromotionStatus | typeof PROMOTION_NOT_FOUND | null>(null);
    const [inProgressPromotionId, setInProgressPromotionId] = useState<string | null>(null);
    const [lastPromotedAt, setLastPromotedAt] = useState<string | null>(null);
    const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);

    const notifyTerminalStatus = (status: PromotionStatus, message?: string) => {
        switch (status) {
            case PromotionStatus.COMPLETED:
                services.messageService.showSuccessBanner(
                    `Successfully promoted ${componentName} to ${stageInfo?.to}.`
                );
                onPromotionComplete?.();
                break;
            case PromotionStatus.PENDING_SUBMISSION_REQUIRES_ACTION:
                services.messageService.showWarningBanner(
                    `Promotion of ${componentName} was completed but the component may require manual action in ${stageInfo?.to}.`
                );
                onPromotionComplete?.();
                break;
            case PromotionStatus.FAILED:
            case PromotionStatus.BLOCKED:
                services.messageService.showErrorBanner(
                    message || `Failed to promote ${componentName} to ${stageInfo?.to}.`
                );
                break;
        }
    };

    useEffect(() => {
        getFeatureFlags().then(flags => {
            setFeatureEnabled(flags.promotionEnabled);
        });
    }, []);

    // ── Initial load: look up the component's latest promotion, silently ────────────────
    useEffect(() => {
        if (!featureEnabled) {
            return;
        }

        if (!componentId) {
            setPromotionStatus(PROMOTION_NOT_FOUND);
            return;
        }

        let cancelled = false;
        services.promotionService.getStatus({ componentId, componentType })
            .then(response => {
                if (cancelled) return;
                setPromotionStatus(response.status);
                setLastPromotedAt(response.completedAt ?? null);
                if (IN_PROGRESS_STATUSES.includes(response.status)) {
                    setInProgressPromotionId(response.promotionId ?? null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPromotionStatus(PROMOTION_NOT_FOUND);
                }
            });
        return () => { cancelled = true; };
    }, [featureEnabled]);

    // ── Polling: runs while inProgressPromotionId is set ────────────────────────────────
    useEffect(() => {
        if (!inProgressPromotionId) {
            return;
        }

        let cancelled = false;
        let attempts = 0;
        let timer: ReturnType<typeof setTimeout> | null = null;

        const poll = async () => {
            attempts += 1;
            try {
                const response = await services.promotionService.getStatus({ promotionId: inProgressPromotionId });
                if (cancelled) return;
                setPromotionStatus(response.status);
                if (response.status === PROMOTION_NOT_FOUND) {
                    setInProgressPromotionId(null);
                    return;
                }
                if (!IN_PROGRESS_STATUSES.includes(response.status)) {
                    setInProgressPromotionId(null);
                    if (response.completedAt) {
                        setLastPromotedAt(response.completedAt);
                    }
                    notifyTerminalStatus(response.status, response.message);
                    return;
                }
            } catch {
                // Transient fetch error — keep polling.
            }
            if (cancelled) return;
            if (attempts >= MAX_PROMOTION_POLL_ATTEMPTS) {
                // Stop tracking so the button doesn't stay stuck in "Promoting" forever;
                // the promotion itself may still be running on the backend.
                setInProgressPromotionId(null);
                services.messageService.showErrorBanner(
                    'Promotion status check timed out. Please check the promotion history.'
                );
                return;
            }
            timer = setTimeout(poll, PROMOTION_POLL_INTERVAL_MS);
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [inProgressPromotionId]);

    const startPromotion = (response: PromoteComponentResponse) => {
        setModalVisible(false);
        setPromotionStatus(response.status);
        if (IN_PROGRESS_STATUSES.includes(response.status)) {
            setInProgressPromotionId(response.promotionId ?? null);
            services.messageService.showInfoBanner(
                `Promotion started successfully for ${componentName}.`
            );
        } else {
            notifyTerminalStatus(response.status, response.message);
        }
    };

    return {
        isPromoting: inProgressPromotionId !== null,
        initializing: promotionStatus === null,
        lastPromotedAt,
        modalVisible,
        openModal: () => setModalVisible(true),
        closeModal: () => setModalVisible(false),
        startPromotion,
        featureEnabled
    };
}
